/**
 * Assembler — blueprint in, body out.
 *
 * Built to the agreed reference (chars_roster.jpg / chars_parts.jpg): chunky
 * boxes and cylinders only, matte cel-shading (`MeshToonNodeMaterial` +
 * GRADIENT_2 via the ToonMaterials factories), identity carried by colour +
 * headgear + silhouette. No logos, no text, nothing thinner than ~0.12 units.
 *
 * ── Two output modes, one set of part numbers ────────────────────────────
 *
 * `assembleCharacter(bp)` builds real Meshes — the mockup and the local player
 * use this. `assembleCharacter(bp, { proxy: true })` builds the SAME pivot
 * hierarchy but every part is a bare Object3D carrying a part descriptor
 * (`{ prim, dims, colorHex, cls, outline, shadow }`) instead of a Mesh; the
 * `CharacterBatcher` renders those through per-part instanced pools, with the
 * part's dimensions composed into the per-instance matrix. Same builders, same
 * numbers, so the pooled body and the naive body cannot drift apart.
 *
 * ── The rig without a skeleton ───────────────────────────────────────────
 *
 *   root (yaw = AIM, lean)
 *    ├─ legsRoot (yaw = MOVE direction)  ← twin-stick split
 *    │    ├─ hips · legL pivot · legR pivot
 *    └─ upper (lunge lean, twist, flinch arch)
 *         ├─ torso (+ vault door / patterns) · armL pivot · armR pivot
 *         ├─ headPivot — head block, face, tell, accessory
 *         └─ back tell (satellite dish)
 *
 * pose.js writes rotations onto these pivots, and on defeat DETACHES the
 * pivots (root.attach) and scatters them — the reference's defeated pose is
 * the toy falling apart into its blocks, which a per-part body gives us
 * almost for free. `rig.scatterUnits` is that list. No bones, no skinning:
 * rigid blocks with visible seams are exactly what the sheet shows.
 *
 * Outline hulls are siblings of each mesh INSIDE its pivot (not CheapOutline's
 * root-level bake), so hulls follow swinging limbs and scattered pieces alike.
 * They are collected on `root.userData.hullMeshes` so the game can hand
 * silhouette duty to the pipeline outline on the tiers that have one.
 */

import * as THREE from 'three/webgpu';
import { positionLocal, normalLocal, float, vec4, clamp, modelViewMatrix } from 'three/tsl';
import { createPlayerToonMaterial, createAccentMaterial } from '../../ToonMaterials.js';

// ─── Shared geometry cache (naive mode) ─────────────────────────

const _geoCache = new Map();

function geo(key, build) {
  let g = _geoCache.get(key);
  if (!g) {
    g = build();
    _geoCache.set(key, g);
  }
  return g;
}

const q = (n) => Math.round(n * 100) / 100;

/**
 * Part descriptors. `prim` + `dims` are what the batcher pools by: a unit
 * primitive with the size restored in the per-instance matrix. `geo()` builds
 * (or fetches) the real-size geometry for naive mode.
 */
function box(w, h, d) {
  return { prim: 'box', dims: [w, h, d], geo: () => geo(`box:${q(w)}x${q(h)}x${q(d)}`, () => new THREE.BoxGeometry(w, h, d)) };
}
function sph(r, seg = 12) {
  return { prim: 'sphere', dims: [r * 2, r * 2, r * 2], geo: () => geo(`sph:${q(r)}:${seg}`, () => new THREE.SphereGeometry(r, seg, seg)) };
}
function cyl(rt, rb, h, seg = 12) {
  // Pool key carries the taper, quantised to quarter steps — the dish, the
  // dial and the cap collapse onto three unit cylinders, not thirty.
  const ratio = rb > 1e-6 ? Math.max(0.25, Math.min(4, Math.round((rt / rb) * 4) / 4)) : 1;
  return {
    prim: `cyl:${ratio.toFixed(2)}`,
    dims: [rb * 2, h, rb * 2],
    geo: () => geo(`cyl:${q(rt)}:${q(rb)}:${q(h)}:${seg}`, () => new THREE.CylinderGeometry(rt, rb, h, seg)),
  };
}
function cone(r, h, seg = 8) {
  return { prim: 'cone', dims: [r * 2, h, r * 2], geo: () => geo(`cone:${q(r)}:${q(h)}:${seg}`, () => new THREE.ConeGeometry(r, h, seg)) };
}

// ─── Colours and materials ──────────────────────────────────────

/** Matte, per the reference. The small per-family emissive is toon FILL for
 *  the shade side under the single key light, not a glow. */
const FAMILY_EMISSIVE = { volt: 0.26, monolith: 0.22, hex: 0.26, mirage: 0.14, talon: 0.24 };

/** One colour + material-class entry per material name the builders use.
 *  `cls` is what the batcher pools by: 'toon' (lit, cel) or 'unlit' (eyes,
 *  glyphs — the accent-material look). */
function paletteFor(bp) {
  const body = new THREE.Color(bp.colorHex);
  return {
    body: { hex: bp.colorHex, cls: 'toon' },
    dark: { hex: body.clone().multiplyScalar(0.55).getHex(), cls: 'toon' },
    trim: { hex: bp.trimHex, cls: 'toon' },
    accent: { hex: bp.accentHex, cls: 'toon' },
    visor: { hex: 0x141a26, cls: 'toon' },
    steel: { hex: 0x9aa7b8, cls: 'toon' },
    white: { hex: 0xffffff, cls: 'unlit' },
    ink: { hex: 0x0b0b18, cls: 'unlit' },
  };
}

function materialsFor(bp) {
  const body = new THREE.Color(bp.colorHex);
  const e = FAMILY_EMISSIVE[bp.family] ?? 0.24;
  return {
    body: createPlayerToonMaterial(body, { emissiveIntensity: e }),
    dark: createPlayerToonMaterial(body.clone().multiplyScalar(0.55), { emissiveIntensity: e * 0.7 }),
    trim: createPlayerToonMaterial(new THREE.Color(bp.trimHex), { emissiveIntensity: 0.18 }),
    accent: createPlayerToonMaterial(new THREE.Color(bp.accentHex), { emissiveIntensity: 0.12 }),
    visor: createPlayerToonMaterial(new THREE.Color(0x141a26), { emissiveIntensity: 0.1 }),
    steel: createPlayerToonMaterial(new THREE.Color(0x9aa7b8), { emissiveIntensity: 0.16 }),
    white: createAccentMaterial(0xffffff),
    ink: createAccentMaterial(0x0b0b18),
  };
}

/**
 * Hull material — PER BODY, not a module singleton. Every teardown path in
 * the client (`removeRemotePlayer`, the gym's `disposeLocalBody`, the
 * mockup's roster rebuild) disposes whatever it traverses; a shared hull
 * material would be disposed by the first teardown out from under every
 * other body still wearing it.
 */
function createHullMaterial() {
  const m = new THREE.MeshBasicNodeMaterial({ color: 0x05060d });
  // CheapOutline's fatten, verbatim: object-space normal push scaled by view
  // depth so the silhouette stays roughly screen-constant at range.
  const viewDepth = modelViewMatrix.mul(vec4(positionLocal, 1)).z.negate();
  const depthScale = clamp(viewDepth.div(10), float(1), float(8));
  m.positionNode = positionLocal.add(normalLocal.mul(float(0.045).mul(depthScale)));
  m.side = THREE.BackSide;
  m.depthWrite = true;
  return m;
}

// ─── Build context: one `mesh()` for both modes ────────────────

function createCtx(bp, proxy) {
  const palette = paletteFor(bp);
  const M = proxy ? null : materialsFor(bp);
  const hullMat = proxy ? null : createHullMaterial();
  const ctx = {
    proxy,
    palette,
    /** proxy mode: every part node, for the batcher. */
    parts: [],
    /** naive mode: every hull mesh, for outline-mode toggling. */
    hulls: [],

    /**
     * One part. `part` is a descriptor from box/sph/cyl/cone; `matName` names
     * a palette entry. Returns a Mesh (naive) or an Object3D proxy (pooled) —
     * both position the same, parent the same, and take children the same.
     */
    mesh(parent, part, matName, x, y, z, { outline = true, shadow = true, name } = {}) {
      if (proxy) {
        const node = new THREE.Object3D();
        node.position.set(x, y, z);
        if (name) node.name = name;
        const p = palette[matName];
        node.userData.part = {
          prim: part.prim,
          dims: part.dims,
          colorHex: p.hex,
          cls: p.cls,
          outline,
          // Unlit parts (eyes, glyphs) never cast in the naive path either.
          shadow: shadow && p.cls === 'toon',
        };
        parent.add(node);
        ctx.parts.push(node);
        return node;
      }
      const m = new THREE.Mesh(part.geo(), M[matName]);
      m.position.set(x, y, z);
      m.castShadow = shadow;
      if (name) m.name = name;
      parent.add(m);
      if (outline) {
        const hull = new THREE.Mesh(part.geo(), hullMat);
        hull.position.copy(m.position);
        hull.userData.isOutlineHull = true;
        hull.castShadow = false;
        m.userData.hull = hull;
        parent.add(hull);
        ctx.hulls.push(hull);
      }
      return m;
    },
  };
  return ctx;
}

/** Mirror a build-time rotation onto a mesh's hull (no-op for proxies). */
function rot(m, x, y, z) {
  m.rotation.set(x, y, z);
  if (m.userData.hull) m.userData.hull.rotation.set(x, y, z);
  return m;
}

/** Mirror a build-time scale onto a mesh's hull (no-op for proxies). */
function scl(m, x, y, z) {
  m.scale.set(x, y, z);
  if (m.userData.hull) m.userData.hull.scale.copy(m.scale);
  return m;
}

function pivot(parent, x, y, z, name) {
  const p = new THREE.Group();
  p.position.set(x, y, z);
  p.name = name;
  parent.add(p);
  return p;
}

// ─── The parts kit (chars_parts.jpg) ────────────────────────────

const TORSO_SIZES = {
  S: { w: 0.62, h: 0.6, d: 0.46 },
  M: { w: 0.78, h: 0.68, d: 0.55 },
  L: { w: 0.98, h: 0.76, d: 0.66 },
};

/** Legs. `legLen` is fixed at 0.5; styles change the SHAPE, not the reach. */
function buildLeg(C, legPiv, style) {
  if (style === 'seg') {
    C.mesh(legPiv, box(0.24, 0.28, 0.28), 'dark', 0, -0.14, 0);
    C.mesh(legPiv, box(0.28, 0.24, 0.34), 'accent', 0, -0.38, 0.03);
  } else if (style === 'slab') {
    C.mesh(legPiv, box(0.3, 0.5, 0.32), 'dark', 0, -0.25, 0);
  } else {
    C.mesh(legPiv, box(0.26, 0.36, 0.3), 'dark', 0, -0.18, 0);
    C.mesh(legPiv, box(0.3, 0.14, 0.4), 'accent', 0, -0.43, 0.04);
  }
}

function buildArm(C, armPiv, style) {
  if (style === 'seg') {
    C.mesh(armPiv, box(0.22, 0.3, 0.24), 'body', 0, -0.12, 0);
    C.mesh(armPiv, box(0.26, 0.26, 0.28), 'dark', 0, -0.4, 0.02);
  } else if (style === 'chunky') {
    C.mesh(armPiv, box(0.34, 0.3, 0.34), 'body', 0, -0.06, 0);
    C.mesh(armPiv, box(0.28, 0.34, 0.3), 'dark', 0, -0.4, 0);
  } else {
    C.mesh(armPiv, box(0.24, 0.5, 0.26), 'body', 0, -0.2, 0);
    C.mesh(armPiv, box(0.28, 0.2, 0.3), 'dark', 0, -0.52, 0);
  }
}

/** Head blocks. Return face placement in headPivot space. */
const HEAD_BLOCKS = {
  cube(C, g) {
    C.mesh(g, box(0.8, 0.68, 0.72), 'body', 0, 0.34, 0);
    return { faceY: 0.36, faceZ: 0.36, topY: 0.68, w: 0.8 };
  },
  tall(C, g) {
    C.mesh(g, box(0.66, 0.86, 0.64), 'body', 0, 0.43, 0);
    return { faceY: 0.5, faceZ: 0.32, topY: 0.86, w: 0.66 };
  },
  rounded(C, g) {
    scl(C.mesh(g, sph(0.42), 'body', 0, 0.38, 0), 1.05, 0.95, 0.98);
    return { faceY: 0.4, faceZ: 0.36, topY: 0.76, w: 0.84 };
  },
  wedge(C, g) {
    C.mesh(g, box(0.78, 0.5, 0.74), 'body', 0, 0.25, 0);
    rot(C.mesh(g, box(0.72, 0.32, 0.6), 'body', 0, 0.56, -0.08), 0.3, 0, 0);
    return { faceY: 0.3, faceZ: 0.37, topY: 0.66, w: 0.78 };
  },
};

// ─── Faces ──────────────────────────────────────────────────────

const NO_HULL = { outline: false, shadow: false };

/** Alive eyes (white + pupil + highlight) plus hidden X-eyes for the defeat
 *  state — the reference's downed robot wears X's. */
function addEyesFace(C, headPivot, head, bp) {
  const styles = { round: { r: 0.12, gap: 0.17 }, wide: { r: 0.14, gap: 0.22 }, narrow: { r: 0.09, gap: 0.14 } };
  const s = styles[bp.eyeStyle] ?? styles.round;
  const eyes = new THREE.Group();
  eyes.name = 'eyes';
  headPivot.add(eyes);
  for (const side of [-1, 1]) {
    const eye = C.mesh(eyes, sph(s.r, 10), 'white', side * s.gap, head.faceY, head.faceZ, NO_HULL);
    const pupil = C.mesh(eye, sph(s.r * 0.55, 8), 'ink', 0, 0, s.r * 0.6, NO_HULL);
    C.mesh(pupil, sph(0.025, 6), 'white', 0.03, 0.03, s.r * 0.45, NO_HULL);
  }
  const mouths = { line: [0.16, 0.04], frown: [0.14, 0.05], open: [0.12, 0.1] };
  const [mw, mh] = mouths[bp.mouth] ?? mouths.line;
  const mouth = C.mesh(eyes, box(mw, mh, 0.03), 'ink', 0, head.faceY - 0.2, head.faceZ + 0.03, NO_HULL);
  if (bp.mouth === 'frown') mouth.rotation.z = 0.25;

  const xEyes = new THREE.Group();
  xEyes.name = 'xEyes';
  xEyes.visible = false;
  headPivot.add(xEyes);
  for (const side of [-1, 1]) {
    for (const tilt of [0.78, -0.78]) {
      const stroke = C.mesh(xEyes, box(0.2, 0.05, 0.03), 'ink', side * s.gap, head.faceY, head.faceZ + s.r * 0.9, NO_HULL);
      stroke.rotation.z = tilt;
    }
  }
}

/** Helmet slit — the bull / vault / android read. Dark strip, no eyes. */
function addVisorFace(C, headPivot, head, { wide = false } = {}) {
  const w = wide ? head.w * 0.8 : head.w * 0.62;
  C.mesh(headPivot, box(w, wide ? 0.3 : 0.14, 0.06), 'visor', 0, head.faceY + 0.02, head.faceZ + 0.02, NO_HULL);
}

// ─── Family tells (locked — see blueprint.js FAMILIES) ─────────

const TELLS = {
  /** BULL: brow ridge + curved-out horns in bone. The reference's helmet read
   *  comes from the visor slit (face:'visor') plus this crest. */
  bullHelm(C, rig, head) {
    C.mesh(rig.headPivot, box(head.w + 0.12, 0.18, 0.5), 'dark', 0, head.faceY + 0.16, head.faceZ - 0.18);
    rot(C.mesh(rig.headPivot, box(0.14, 0.3, head.w * 0.8), 'dark', 0, head.topY + 0.1, -0.05), 0.15, 0, 0);
    for (const side of [-1, 1]) {
      const horn = C.mesh(rig.headPivot, cone(0.11, 0.5, 8), 'trim', side * (head.w / 2 + 0.1), head.topY - 0.05, 0);
      rot(horn, 0, 0, side * -0.9);
    }
  },

  /** VAULT: the torso IS a safe — recessed door, dial with spokes, hinges. */
  vaultTorso(C, rig, head, bp, torso) {
    const doorZ = torso.d / 2;
    C.mesh(rig.upper, box(torso.w - 0.2, torso.h - 0.2, 0.08), 'dark', 0, torso.midY, doorZ);
    const dial = C.mesh(rig.upper, cyl(0.15, 0.15, 0.14, 10), 'trim', 0, torso.midY, doorZ + 0.06);
    rot(dial, Math.PI / 2, 0, 0);
    for (const a of [0, Math.PI / 2]) {
      const spoke = C.mesh(rig.upper, box(0.34, 0.06, 0.05), 'trim', 0, torso.midY, doorZ + 0.12, { outline: false });
      spoke.rotation.z = a;
    }
    for (const side of [-1, 1]) {
      C.mesh(rig.upper, box(0.12, 0.3, 0.12), 'trim', side * (torso.w / 2 + 0.02), torso.midY, doorZ - 0.06);
    }
  },

  /** BEAR: ears + muzzle + brow ON the head block. The head is the tell. */
  bearHead(C, rig, head) {
    for (const side of [-1, 1]) {
      C.mesh(rig.headPivot, box(0.22, 0.2, 0.14), 'dark', side * (head.w / 2 - 0.12), head.topY + 0.06, -0.05);
    }
    C.mesh(rig.headPivot, box(0.34, 0.26, 0.18), 'trim', 0, head.faceY - 0.14, head.faceZ + 0.06);
    C.mesh(rig.headPivot, box(0.14, 0.1, 0.06), 'ink', 0, head.faceY - 0.08, head.faceZ + 0.18, NO_HULL);
    for (const side of [-1, 1]) {
      const brow = C.mesh(rig.headPivot, box(0.16, 0.05, 0.04), 'ink', side * 0.17, head.faceY + 0.13, head.faceZ + 0.03, NO_HULL);
      brow.rotation.z = side * -0.35;
    }
  },

  /** ANDROID: chrome dome over the rounded head + antenna. */
  chromeDome(C, rig, head) {
    scl(C.mesh(rig.headPivot, sph(0.46), 'trim', 0, head.faceY, -0.03), 1, 0.95, 1);
    // The big dark faceplate the reference shows instead of a slit.
    const plate = C.mesh(rig.headPivot, sph(0.4), 'visor', 0, head.faceY, 0.1, { outline: false });
    plate.scale.set(0.82, 0.7, 0.82);
    C.mesh(rig.headPivot, box(0.12, 0.3, 0.12), 'trim', 0.2, head.topY + 0.16, 0);
  },

  /** SATBOT: shoulder satellite dish + head antenna. */
  satDish(C, rig, head, bp, torso) {
    C.mesh(rig.upper, box(0.12, 0.5, 0.12), 'trim', -0.34, torso.topY + 0.2, -torso.d / 2 - 0.1);
    // Steel, always — a seeded accent here turned the dish into dark "ears"
    // from the front. The dish is a TELL; tells do not take seed colours.
    const dish = C.mesh(rig.upper, cyl(0.36, 0.14, 0.14, 12), 'steel', -0.42, torso.topY + 0.5, -torso.d / 2 - 0.14);
    rot(dish, -0.9, 0, -0.35);
    C.mesh(rig.headPivot, box(0.12, 0.34, 0.12), 'trim', 0.18, head.topY + 0.15, 0);
  },
};

// ─── Accessories (variation axis — never competes with a tell) ──

const ACCESSORIES = {
  none() {},
  shoulderPlate(C, rig, head, torso) {
    C.mesh(rig.upper, box(0.36, 0.16, 0.44), 'accent', torso.w / 2 + 0.12, torso.topY + 0.02, 0);
  },
  cap(C, rig, head) {
    C.mesh(rig.headPivot, cyl(0.34, 0.37, 0.16, 10), 'accent', 0, head.topY + 0.06, 0);
    C.mesh(rig.headPivot, box(0.4, 0.12, 0.34), 'accent', 0, head.topY + 0.01, 0.34);
  },
  antenna(C, rig, head) {
    C.mesh(rig.headPivot, box(0.12, 0.3, 0.12), 'trim', -0.18, head.topY + 0.13, 0);
    C.mesh(rig.headPivot, sph(0.07, 8), 'trim', -0.18, head.topY + 0.32, 0, NO_HULL);
  },
  visorGlasses(C, rig, head) {
    C.mesh(rig.headPivot, box(head.w * 0.9, 0.14, 0.08), 'visor', 0, head.faceY + 0.16, head.faceZ + 0.03, { outline: false });
  },
};

// ─── The assembler ──────────────────────────────────────────────

/**
 * Blueprint → body. Root spans −1..+1 in y like the sim capsule
 * (PLAYER_RADIUS 0.5, PLAYER_HALF_HEIGHT 1.0) — appearance NEVER changes the
 * capsule; styles trade mass inside the same envelope.
 *
 * @param {object} bp blueprint from `blueprintFor`
 * @param {{ proxy?: boolean }} [opts] `proxy: true` builds batcher proxies
 *   instead of Meshes — see the header.
 */
export function assembleCharacter(bp, { proxy = false } = {}) {
  const C = createCtx(bp, proxy);
  const t = TORSO_SIZES[bp.torsoSize] ?? TORSO_SIZES.M;

  const root = new THREE.Group();
  root.rotation.order = 'YXZ';

  const LEG_LEN = 0.5;
  const hipY = -1 + LEG_LEN + 0.12;
  const torso = {
    w: t.w,
    d: t.d,
    h: t.h,
    baseY: hipY + 0.1,
    topY: hipY + 0.1 + t.h,
    midY: hipY + 0.1 + t.h / 2,
  };

  const legsRoot = pivot(root, 0, 0, 0, 'legsRoot');
  C.mesh(legsRoot, box(t.w * 0.76, 0.24, t.d * 0.82), 'accent', 0, hipY, 0);
  const legL = pivot(legsRoot, -0.18, -1 + LEG_LEN, 0, 'legL');
  const legR = pivot(legsRoot, 0.18, -1 + LEG_LEN, 0, 'legR');
  buildLeg(C, legL, bp.legStyle);
  buildLeg(C, legR, bp.legStyle);

  const upper = pivot(root, 0, 0, 0, 'upper');
  C.mesh(upper, box(t.w, t.h, t.d), 'body', 0, torso.midY, 0);

  const armY = torso.topY - 0.08;
  const armL = pivot(upper, -(t.w / 2 + 0.16), armY, 0, 'armL');
  const armR = pivot(upper, t.w / 2 + 0.16, armY, 0, 'armR');
  buildArm(C, armL, bp.armStyle);
  buildArm(C, armR, bp.armStyle);

  // Pattern — the seed's mark on the torso (skipped for the vault: the door
  // owns that face). Thin but never under 0.12 in its smallest dimension...
  // except proud overlays, which follow the props' cap-rail precedent.
  if (bp.tell !== 'vaultTorso') {
    if (bp.pattern === 'belt') {
      C.mesh(upper, box(t.w + 0.06, 0.14, t.d + 0.06), 'accent', 0, torso.baseY + 0.1, 0);
    } else if (bp.pattern === 'stripe') {
      C.mesh(upper, box(0.16, t.h - 0.12, 0.12), 'accent', 0, torso.midY, t.d / 2 - 0.02);
    } else if (bp.pattern === 'panel') {
      C.mesh(upper, box(t.w - 0.26, t.h - 0.26, 0.12), 'dark', 0, torso.midY, t.d / 2 - 0.02);
    }
  }

  const headPivot = pivot(upper, 0, torso.topY + 0.04, 0, 'headPivot');
  const rig = { root, legsRoot, legL, legR, upper, armL, armR, headPivot };
  const head = HEAD_BLOCKS[bp.headBlock]?.(C, headPivot) ?? HEAD_BLOCKS.cube(C, headPivot);

  if (bp.face === 'eyes') addEyesFace(C, headPivot, head, bp);
  else addVisorFace(C, headPivot, head, { wide: bp.tell === 'chromeDome' });

  TELLS[bp.tell]?.(C, rig, head, bp, torso);
  ACCESSORIES[bp.accessory]?.(C, rig, head, torso);

  // What the defeat scatter breaks the toy into. Order matters to nobody;
  // membership does: every visible part must live under exactly one unit.
  rig.scatterUnits = [legL, legR, armL, armR, headPivot, upper, legsRoot];

  root.userData.blueprint = bp;
  root.userData.rig = rig;
  root.userData.colorHex = bp.colorHex;
  // Where a crown belongs: just over the head block's top, whatever block the
  // seed drew. PlayerVisuals reads this instead of assuming one head height.
  root.userData.crownY = torso.topY + 0.04 + head.topY + 0.14;
  if (proxy) root.userData.parts = C.parts;
  else root.userData.hullMeshes = C.hulls;
  return root;
}
